import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useRecoilState } from 'recoil';
import dayjs from 'dayjs';
import { Table, Spin, Empty, Button, Select as AntSelect, message } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSyncAlt, faCopy } from '@fortawesome/pro-duotone-svg-icons';
import { Bar, Line } from 'react-chartjs-2';
import { notificationsAtom } from '../../../../atoms/Atoms';
import {
  DAYS_WINDOW_OPTIONS,
  getPipelineHealthDomains,
  getPipelineHealthEntityTypes,
  getPipelineHealthErrors
} from '../../../../api/pipelineHealthApi';
import {
  formatDomain,
  DOMAIN_COLORS,
  FALLBACK_COLOR
} from '../pipelineHealthUtils';
import { thousand } from '../../../../functions';

// Truncate long error messages for the chart's Y labels; the full message
// is still available in the tooltip.
function truncateMessage(messageToTruncate, max = 50) {
  if (!messageToTruncate) return '';
  return messageToTruncate.length <= max ? messageToTruncate : `${messageToTruncate.slice(0, max - 1)}…`;
}

function ErrorAnalysis() {
  const { t } = useTranslation();
  const [notifications, setNotifications] = useRecoilState(notificationsAtom);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [domains, setDomains] = useState([]);
  const [loadingDomains, setLoadingDomains] = useState(false);
  const [domain, setDomain] = useState('');
  const [topMessages, setTopMessages] = useState(10);
  const [daysWindow, setDaysWindow] = useState(7);
  const [entities, setEntities] = useState([]);
  const [entityFilter, setEntityFilter] = useState('');

  const pushError = (description) => {
    setNotifications([...notifications, { title: t('error'), description, error: true }]);
  };

  // Fetch the domain list once on mount so the user can switch the incident
  // timeline to any domain (HMS by default, matching the PDF).
  useEffect(() => {
    setLoadingDomains(true);
    getPipelineHealthDomains()
      .then((res) => {
        const list = res?.data?.data ?? [];
        setDomains(list);
      })
      .catch((err) => pushError(err?.response?.data?.message || err.message))
      .finally(() => setLoadingDomains(false));
  }, []);

  // Entities feed the optional entity filter. Re-fetched whenever the
  // incident domain changes so the dropdown shows only entities from that
  // domain. /entityTypes returns [{type, entities}]; flatten + dedupe for
  // a single-list dropdown. When the previously-picked entity is no longer
  // in the new list, reset back to "All entities".
  useEffect(() => {
    getPipelineHealthEntityTypes(domain)
      .then((res) => {
        const groups = res?.data?.data ?? [];
        const list = Array.from(
          new Set(groups.flatMap((g) => g.entities || []))
        ).sort();
        setEntities(list);
        if (entityFilter && !list.includes(entityFilter)) setEntityFilter('');
      })
      .catch((err) => pushError(err?.response?.data?.message || err.message));
  }, [domain]);

  const load = () => {
    setLoading(true);
    getPipelineHealthErrors(daysWindow, domain, topMessages, entityFilter)
      .then((res) => {
        if (res?.data?.success === false) {
          pushError(res?.data?.error || t('error'));
          return;
        }
        setData(res?.data?.data ?? res?.data);
      })
      .catch((err) => pushError(err?.response?.data?.message || err.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [domain, topMessages, daysWindow, entityFilter]);

  const daysOptions = DAYS_WINDOW_OPTIONS.map((d) => ({
    value: d,
    label: t(`pipeline_health_days_${d}`)
  }));
  const entityOptions = [
    { value: '', label: t('pipeline_health_all_entities') },
    ...entities.map((e) => ({ value: e, label: e }))
  ];

  // ---- Top messages bar chart ------------------------------------------

  const messagesChart = useMemo(() => {
    const rows = data?.topMessages || [];
    if (rows.length === 0) return null;
    // Backend returns rows already sorted desc by count, but make it
    // explicit so the bars read top-down by frequency.
    const sorted = [...rows].sort((a, b) => Number(b.count) - Number(a.count));
    return {
      // Short truncation so the chart's left-side label area can't force
      // the canvas to grow wider than its container.
      labels: sorted.map((r) => truncateMessage(r.message, 35)),
      datasets: [
        {
          label: t('pipeline_health_count'),
          data: sorted.map((r) => Number(r.count) || 0),
          backgroundColor: '#dc2626',
          borderColor: '#dc2626',
          raw: sorted
        }
      ]
    };
  }, [data, t]);

  const messagesOptions = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    indexAxis: 'y',
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          title: (ctxs) => {
            const row = messagesChart?.datasets?.[0]?.raw?.[ctxs[0].dataIndex];
            return row?.message || '';
          },
          label: (ctx) => `${t('pipeline_health_count')}: ${thousand(ctx.parsed.x)}`
        }
      }
    },
    scales: {
      // Log scale matches the PDF — the highest-frequency message dominates
      // otherwise (47k vs <100 for the tail).
      x: {
        type: 'logarithmic',
        title: { display: true, text: `${t('pipeline_health_count')} (log)` }
      },
      y: { ticks: { font: { size: 10 } } }
    }
  }), [messagesChart, t]);

  const messagesHeight = Math.max(280, ((data?.topMessages || []).length) * 26 + 60);

  // ---- Incident timeline -----------------------------------------------

  const timelineChart = useMemo(() => {
    const points = data?.incidentTimeline || [];
    if (points.length === 0) return null;
    const sorted = [...points].sort((a, b) => dayjs(a.date).valueOf() - dayjs(b.date).valueOf());
    const colour = DOMAIN_COLORS[data?.domain] || FALLBACK_COLOR;
    return {
      labels: sorted.map((p) => dayjs(p.date).format('MMM DD')),
      datasets: [
        {
          label: formatDomain(data?.domain),
          data: sorted.map((p) => Number(p.avgHealth)),
          borderColor: colour,
          backgroundColor: colour,
          tension: 0.25,
          pointRadius: 1.5,
          fill: false,
          spanGaps: true
        }
      ]
    };
  }, [data]);

  const timelineOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: { legend: { position: 'bottom' } },
    scales: {
      y: { min: 0, max: 100, ticks: { stepSize: 25 } },
      x: { ticks: { autoSkip: true, maxTicksLimit: 12, font: { size: 9 } } }
    }
  };

  // ---- Domain summary table --------------------------------------------

  // Copy helper — uses the modern async clipboard API when available, falls
  // back to a hidden textarea for older browsers / non-HTTPS contexts.
  const copyToClipboard = (text) => {
    if (!text) return;
    const done = (ok) => {
      if (ok) message.success(t('copied_to_clipboard'));
      else message.error(t('error'));
    };
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(text).then(() => done(true)).catch(() => done(false));
      return;
    }
    try {
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      done(true);
    } catch {
      done(false);
    }
  };

  const summaryColumns = [
    {
      title: t('domain'),
      dataIndex: 'domain',
      key: 'domain',
      width: 140,
      render: (d) => (
        <span className="font-semibold" style={{ color: DOMAIN_COLORS[d] || FALLBACK_COLOR }}>
          {formatDomain(d)}
        </span>
      )
    },
    {
      title: t('pipeline_health_errors_main'),
      dataIndex: 'mainError',
      key: 'mainError',
      // Truncate long messages so the table keeps its width; `showTitle`
      // gives a native browser tooltip with the full text on hover.
      ellipsis: { showTitle: true },
      render: (v) => v ? <code className="text-xs text-gray-600">{v}</code> : '—'
    },
    {
      title: '',
      key: 'copy',
      width: 48,
      align: 'center',
      render: (_, row) => (
        row.mainError
          ? (
            <Button
              size="small"
              type="text"
              onClick={() => copyToClipboard(row.mainError)}
              icon={<FontAwesomeIcon icon={faCopy} />}
              title={t('copy_to_clipboard')}
            />
          )
          : null
      )
    },
    {
      title: t('pipeline_health_diagnostic'),
      dataIndex: 'diagnostic',
      key: 'diagnostic',
      width: 260,
      render: (v) => v || '—'
    }
  ];

  // ----------------------------------------------------------------------

  const byDomain = data?.byDomain || [];
  const topMessagesList = data?.topMessages || [];

  return (
    <div className="flex flex-col h-full">
      <div className="flex flex-wrap items-center gap-3 px-2 pb-2">
        <span className="text-sm text-gray-600">{t('pipeline_health_days_window')}:</span>
        <AntSelect
          value={daysWindow}
          disabled={loading}
          onChange={setDaysWindow}
          style={{ width: 150 }}
          options={daysOptions}
        />
        <span className="text-sm text-gray-600">{t('domain')}:</span>
        <AntSelect
          value={domain}
          disabled={loading || loadingDomains}
          onChange={setDomain}
          style={{ minWidth: 160 }}
          options={[
            { value: '', label: t('pipeline_health_all_domains') },
            ...domains.map((d) => ({ value: d, label: formatDomain(d) }))
          ]}
        />
        <span className="text-sm text-gray-600">{t('entity')}:</span>
        <AntSelect
          showSearch
          value={entityFilter}
          disabled={loading}
          onChange={setEntityFilter}
          style={{ minWidth: 200 }}
          options={entityOptions}
          optionFilterProp="label"
        />
        <span className="text-sm text-gray-600">{t('pipeline_health_volume_top_n_label')}:</span>
        <AntSelect
          value={topMessages}
          disabled={loading}
          onChange={setTopMessages}
          style={{ width: 80 }}
          options={[
            { value: 5, label: '5' },
            { value: 10, label: '10' },
            { value: 15, label: '15' },
            { value: 20, label: '20' }
          ]}
        />
        {data?.datasetStart && data?.datasetEnd && (
          <span className="text-sm text-gray-500">
            {dayjs(data.datasetStart).format('MMM DD, YYYY')} - {dayjs(data.datasetEnd).format('MMM DD, YYYY')}
          </span>
        )}
        {data?.datasetStart && !data?.datasetEnd && (
          <span className="text-sm text-gray-500">
            {dayjs(data.datasetStart).format('MMM DD, YYYY')} - {dayjs(data.datasetStart).add(30, 'days').format('MMM DD, YYYY')}
          </span>
        )}
        {!data?.datasetStart && data?.datasetEnd && (
          <span className="text-sm text-gray-500">
            {dayjs(data.datasetEnd).add(-30, 'days').format('MMM DD, YYYY')} - {dayjs(data.datasetEnd).format('MMM DD, YYYY')}
          </span>
        )}
        <Button
          className="ml-auto"
          type="primary"
          onClick={load}
          loading={loading}
          icon={<FontAwesomeIcon icon={faSyncAlt} />}
        >
          {t('refresh')}
        </Button>
      </div>

      {loading && !data ? (
        <div className="flex items-center justify-center py-10"><Spin /></div>
      ) : !data ? (
        <Empty description={t('no_data')} />
      ) : (
        <div className="px-2 pb-4 space-y-4 min-w-0">

          {/* Top error messages */}
          <div className="bg-white rounded shadow-sm p-3 min-w-0 overflow-hidden">
            <div className="text-sm font-semibold mb-2">
              {t('pipeline_health_errors_top_messages')} ({topMessages})
            </div>
            {topMessagesList.length === 0 ? (
              <Empty description={t('no_data')} />
            ) : (
              <Spin spinning={loading}>
                <div style={{ height: messagesHeight, position: 'relative', width: '100%' }}>
                  <Bar data={messagesChart} options={messagesOptions} />
                </div>
              </Spin>
            )}
          </div>

          {/* Incident timeline */}
          <div className="bg-white rounded shadow-sm p-3 min-w-0 overflow-hidden">
            <div className="text-sm font-semibold mb-2">
              {t('pipeline_health_errors_incident_timeline')} — {formatDomain(data.domain)}
            </div>
            {!timelineChart ? (
              <Empty description={t('no_data')} />
            ) : (
              <Spin spinning={loading}>
                <div style={{ height: 260, position: 'relative', width: '100%' }}>
                  <Line data={timelineChart} options={timelineOptions} />
                </div>
              </Spin>
            )}
          </div>

          {/* Per-domain summary */}
          <div className="bg-white rounded shadow-sm p-3 min-w-0 overflow-hidden">
            <div className="text-sm font-semibold mb-2">{t('pipeline_health_top_error_domain_summary')}</div>
            {byDomain.length === 0 ? (
              <Empty description={t('no_data')} />
            ) : (
              <Table
                loading={loading}
                size="small"
                rowKey="domain"
                columns={summaryColumns}
                dataSource={byDomain}
                pagination={false}
                tableLayout="fixed"
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default ErrorAnalysis;
